import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Leaf, Flame, ArrowUpRight, Zap, Target, Gauge } from 'lucide-react';

const ProductCard = ({ title, desc, icon: Icon, features, color, image, onDetail }) => {
  return (
    <motion.div 
      whileHover={{ y: -10 }}
      transition={{ duration: 0.4 }}
      className={`relative group bg-[#111111] p-10 rounded-[40px] border border-white/5 overflow-hidden transition-all duration-500 hover:border-${color}/30`}
    >
      <div className="absolute inset-x-0 top-0 h-48 overflow-hidden opacity-30 group-hover:opacity-50 transition-opacity">
         <img src={image} alt={title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
      </div>

      <div className="relative z-10 pt-24 text-left">
        <div className={`w-16 h-16 bg-${color}/20 rounded-2xl flex items-center justify-center text-${color} mb-8 group-hover:scale-110 transition-transform`}>
          <Icon size={32} fill="currentColor" />
        </div>
        
        <h3 className="text-2xl font-extrabold syne mb-6 tracking-tight group-hover:text-white group-hover:translate-x-2 transition-all duration-300">
           {title}
        </h3>
        
        <p className="text-slate-400 text-lg mb-10 max-w-sm line-clamp-3 leading-relaxed">
          {desc}
        </p>

        <div className="space-y-4 mb-10">
           {features.map((f, i) => (
             <div key={i} className="flex items-center gap-3 text-slate-300 group-hover:text-white transition-colors">
                <div className={`w-1.5 h-1.5 rounded-full bg-${color}`}></div>
                <span className="text-sm font-semibold tracking-wide uppercase font-body">{f}</span>
             </div>
           ))}
        </div>

        <button 
          onClick={onDetail}
          className={`w-full py-4 bg-white/5 group-hover:bg-${color} group-hover:text-white rounded-2xl flex items-center justify-center gap-3 font-black syne tracking-widest transition-all duration-300 text-sm`}
        >
           LEARN MORE
           <ArrowUpRight size={18} />
        </button>
      </div>
    </motion.div>
  );
};

const Products = ({ onShowDetail }) => {
  const products = [
    {
      title: "Bio Mass Pellets",
      desc: "Uniform wood pellets engineered for high-intensity heating and minimal residue. The perfect sustainable choice for industrial boilers and residential heating. Our pellets have high energy density and are carbon neutral.",
      icon: Leaf,
      features: ["98% Energy Density", "Low Ash (< 1%)", "Carbon Neutral", "Easy Logistics"],
      color: "primary",
      image: "/images/pellets.png",
      gallery: [
         "/images/saw_dust_pellets.png",
         "/images/agro_waste_pellets.png",
         "/images/8mm_pellets.png"
      ]
    },
    {
      title: "Bio Coal (Briquettes)",
      desc: "Premium 90mm bio coal briquettes manufactured to the highest standards. Expertly produced from high-quality agro waste and saw dust. A powerful, sustainable alternative to traditional coal with superior burning properties.",
      icon: Flame,
      features: ["3400-4400 kcal/kg", "Smoke-free Burn", "Industrial Grade", "Eco-Certified"],
      color: "secondary",
      image: "/images/90mm_bio_coal_briquettes.png",
      gallery: [
         "/images/saw_dust_briquette_90mm.png",
         "/images/agro_waste_briquette.png",
         "/images/coal.png"
      ]
    }
  ];

  return (
    <section id="products" className="py-24 px-6 relative bg-[#080808] overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8 relative z-10">
           <div className="max-w-2xl text-left">
              <span className="text-primary font-bold tracking-[0.2em] mb-4 block uppercase text-sm">Industrial Solutions</span>
              <h2 className="text-2xl md:text-5xl font-black syne tracking-tighter">
                Premium Bio <br />
                <span className="text-slate-400 italic text-xl md:text-3xl block mt-2">Energy Range.</span>
              </h2>
           </div>
           <p className="text-slate-500 font-medium max-w-sm mb-4 text-left">
              Advanced technology meets raw nature to deliver high-calorific products that reduce your carbon footprint without compromising on performance.
           </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {products.map((p, idx) => (
            <ProductCard key={idx} {...p} onDetail={() => onShowDetail(p)} />
          ))}
        </div>

        {/* Stats Section Overlay style */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-4">
           {[
             { label: "Calorific Value", value: "3800-4800", sub: "kcal/kg", icon: Flame },
             { label: "Moisture Content", value: "<10%", sub: "Standard", icon: Gauge },
             { label: "Carbon Neutral", value: "100%", sub: "Sustainable", icon: Target },
             { label: "Production Capacity", value: "50T", sub: "Daily", icon: Zap },
           ].map((s, i) => (
             <motion.div 
               key={i} 
               whileHover={{ scale: 1.02 }}
               className="glass p-8 rounded-[30px] border border-white/5 flex flex-col items-center text-center group"
             >
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all mb-4">
                   <s.icon size={20} />
                </div>
                <p className="text-3xl font-black syne group-hover:scale-110 transition-transform">{s.value}</p>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1 mb-2">{s.label}</p>
                <p className="text-sm text-slate-400 font-bold">{s.sub}</p>
             </motion.div>
           ))}
        </div>
      </div>

      {/* Background Shapes */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-64 h-64 bg-primary/10 blur-[100px] -z-10 rounded-full"></div>
    </section>
  );
};

export default Products;
